// Shared FIN-ARON widgets: quarterly rollup table + project-inflow datapoints
// ("kiedy spłyną": termin umowy vs realne prognozy).

import { Link } from "react-router-dom";

import { CashflowChart } from "@/features/finances/components/CashflowChart";
import { SectionCard } from "@/features/power_projects/components/ui";
import type { CashflowEvent } from "@/features/enterprises/types";
import { formatDate } from "@/utils/formatDate";

import type { CompletionDatapoint, QuarterRow } from "./types";
import { DeadlineFlag, fmtPln, HELP, InfoDot, ProgressBar, SlipBadge } from "./ui";

export function QuartersTable({ quarters }: { quarters: QuarterRow[] }) {
  if (quarters.length === 0)
    return <p className="text-sm text-gray-400">Brak przyszłych przepływów do pokazania.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-gray-500 border-b border-[var(--color-border)]">
          <th className="py-2 pr-3">Kwartał</th>
          <th className="py-2 pr-3 text-right">Wpływy</th>
          <th className="py-2 pr-3 text-right">Wydatki</th>
          <th className="py-2 pr-3 text-right">Netto</th>
          <th className="py-2 text-right">Narastająco</th>
        </tr>
      </thead>
      <tbody>
        {quarters.map((q) => (
          <tr key={q.quarter} className="border-b border-[var(--color-border)]">
            <td className="py-2 pr-3 font-medium">{q.quarter}</td>
            <td className="py-2 pr-3 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
              {fmtPln(q.cash_in)}
            </td>
            <td className="py-2 pr-3 text-right tabular-nums text-red-600 dark:text-red-400">
              {fmtPln(q.cash_out)}
            </td>
            <td className={`py-2 pr-3 text-right tabular-nums font-semibold ${Number(q.net) < 0 ? "text-red-600 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400"}`}>
              {fmtPln(q.net)}
            </td>
            <td className={`py-2 text-right tabular-nums ${Number(q.cumulative_net) < 0 ? "text-red-600 dark:text-red-400" : "text-emerald-700 dark:text-emerald-400"}`}>
              {fmtPln(q.cumulative_net)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DatapointsTable({
  datapoints,
  showDesigner = true,
}: {
  datapoints: CompletionDatapoint[];
  showDesigner?: boolean;
}) {
  if (datapoints.length === 0)
    return <p className="text-sm text-gray-400">Brak aktywnych projektów.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-500 border-b border-[var(--color-border)]">
            <th className="py-2 pr-3">Projekt</th>
            {showDesigner && <th className="py-2 pr-3">Koordynator</th>}
            <th className="py-2 pr-3 text-right">Wartość</th>
            <th className="py-2 pr-3">Termin umowy</th>
            <th className="py-2 pr-3">Prognoza (opt / neut / pes)<InfoDot text={HELP.variants} /></th>
            <th className="py-2 pr-3">Poślizg (neut)<InfoDot text={HELP.slip} /></th>
            <th className="py-2 pr-3">Postęp<InfoDot text={HELP.progress} /></th>
            <th className="py-2 pr-3 text-right">Marża</th>
            <th className="py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {datapoints.map((d) => (
            <tr key={d.connection_id} className="border-b border-[var(--color-border)] hover:bg-black/5 dark:hover:bg-white/5">
              <td className="py-2 pr-3 max-w-64 truncate" title={d.name}>
                <Link to={`/power-projects/${d.project_id}`} className="text-sky-600 dark:text-sky-400 hover:underline">
                  {d.name}
                </Link>
              </td>
              {showDesigner && (
                <td className="py-2 pr-3">
                  <Link to={`/fin-aron/person/${d.designer_id}`} className="text-sky-600 dark:text-sky-400 hover:underline">
                    {d.designer}
                  </Link>
                </td>
              )}
              <td className="py-2 pr-3 text-right tabular-nums">{fmtPln(d.total_value)}</td>
              <td className="py-2 pr-3 tabular-nums">{formatDate(d.contract_deadline)}</td>
              <td className="py-2 pr-3 tabular-nums text-xs">
                {formatDate(d.completion_dates.optimistic)} / <b>{formatDate(d.completion_dates.neutral)}</b> / {formatDate(d.completion_dates.pessimistic)}
              </td>
              <td className="py-2 pr-3"><SlipBadge days={d.slip_days_neutral} /></td>
              <td className="py-2 pr-3"><ProgressBar pct={d.milestones_pct} /></td>
              <td className={`py-2 pr-3 text-right tabular-nums font-medium ${d.margin != null && Number(d.margin) < 0 ? "text-red-600 dark:text-red-400" : ""}`}>
                {fmtPln(d.margin)}
              </td>
              <td className="py-2"><DeadlineFlag flag={d.deadline_flag} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// default: bieżący miesiąc + 9 miesięcy analizy w przód
function monthStr(offset: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function CashflowSection({
  events,
  quarters,
  storageKey,
}: {
  events: CashflowEvent[];
  quarters: QuarterRow[];
  storageKey: string;
}) {
  return (
    <>
      <SectionCard
        title={<>Symulacja przepływów (prognoza spływów, pensje, narzut, podwykonawcy)<InfoDot text={HELP.cashflow} /></>}
        className="mb-4"
      >
        <CashflowChart
          events={events}
          storageKey={storageKey}
          monthMode
          initialMonthFrom={monthStr(0)}
          initialMonthTo={monthStr(8)}
        />
      </SectionCard>
      <SectionCard title={<>Kwartały — analiza przyszłych przepływów<InfoDot text={HELP.quarters} /></>}>
        <QuartersTable quarters={quarters} />
      </SectionCard>
    </>
  );
}
